import React, { useState, useRef } from "react";
import { Modal, ModalHeader, ModalBody, Form, FormGroup, Input, Label, Button, Alert } from "reactstrap";
import { FixedSizeList as List } from 'react-window';
import PropTypes from 'prop-types';

// Custom styles
const styles = {
    authForm: {
        background: '#ffffff',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
    },
    avatarLg: {
        width: '72px',
        height: '72px',
        margin: '0 auto',
    },
    avatarTitle: {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        background: '#f8f9fa',
        color: '#556ee6',
    },
    inputGroup: {
        transition: 'all 0.2s ease',
        '&:focus-within': {
            boxShadow: '0 0 0 3px rgba(85,110,230,0.1)',
        },
    },
};

const ModelUserPermission = ({
    isOpen,
    toggle,
    title = "Gross Profit Variance Notice",
    data = [],
    onConfirm,
    loading = false,
}) => {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const usernameRef = useRef();

    // Calculate pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = data.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(data.length / itemsPerPage);

    // Reset form and pagination when modal is opened/closed
    React.useEffect(() => {
        if (!isOpen) {
            setUsername("");
            setPassword("");
            setError("");
            setCurrentPage(1);
        } else {
            // Focus username input when modal opens
            setTimeout(() => usernameRef.current?.focus(), 0);
        }
    }, [isOpen]);

    const handleSubmit = (e) => {
        e?.preventDefault();
        if (!username.trim()) {
            setError("Please enter username");
            return;
        }
        if (!password.trim()) {
            setError("Please enter password");
            return;
        }

        //filter out any leading or trailing spaces
        //filter sql injection
        const filteredUsername = username.trim().replace(/['";]/g, "");
        const filteredPassword = password.trim().replace(/['";]/g, "");

        onConfirm(filteredUsername, filteredPassword);
    };

    const renderPaginationButtons = () => {
        const buttons = [];
        for (let i = 1; i <= totalPages; i++) {
            buttons.push(
                <Button
                    key={i}
                    color={currentPage === i ? "primary" : "light"}
                    size="sm"
                    onClick={() => setCurrentPage(i)}
                    className="mx-1"
                >
                    {i}
                </Button>
            );
        }
        return buttons;
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="xl" centered={true} backdrop="static">
            <ModalHeader toggle={() => {
                if (!loading) {
                    toggle();
                }
            }} className="bg-light">

                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="text-dark mb-0 flex-grow-1 me-3">
                        <i className="mdi mdi-alert-circle-outline me-2 text-warning"></i>
                        {title}
                    </h5>
                    <span className="badge bg-info">
                        Showing {data.length} items with GP variance
                    </span>
                </div>


            </ModalHeader>
            <ModalBody className="px-4 py-4">
                <div className="d-flex gap-4">
                    {/* GP Variance Table Section */}
                    <div className="flex-grow-1">
                        <div className="border rounded bg-light">
                            <div className="table-responsive" style={{ maxHeight: '400px' }}>
                                <table className="table table-sm table-bordered mb-0">
                                    <thead className="table-light sticky-top">
                                        <tr>
                                            <th>#</th>
                                            <th>Item</th>
                                            <th width="100" className="text-center">Cost</th>
                                            <th width="100" className="text-center">Selling</th>
                                            <th width="100" className="text-center">Current GP%</th>
                                            <th width="100" className="text-center">Fix GP%</th>
                                            <th width="100" className="text-center">Variance</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.map((item, index) => (
                                            <tr key={index}>
                                                <td>{item.lineNo}</td>
                                                <td>{item.stockCode + ' - ' + item.proName}</td>
                                                <td className="text-center">{item.cost}</td>
                                                <td className="text-center">{item.selling}</td>
                                                <td className="text-center">{item.currentGP}%</td>
                                                <td className="text-center">{item.fixGP}%</td>
                                                <td className="text-center text-danger">
                                                    {(item.currentGP - item.fixGP).toFixed(2)}%
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center p-2 border-top">
                                    <Button
                                        color="light"
                                        size="sm"
                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                        disabled={currentPage === 1}
                                    >
                                        <i className="mdi mdi-chevron-left" />
                                    </Button>
                                    <div className="d-flex">
                                        {renderPaginationButtons()}
                                    </div>
                                    <Button
                                        color="light"
                                        size="sm"
                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                        disabled={currentPage === totalPages}
                                    >
                                        <i className="mdi mdi-chevron-right" />
                                    </Button>
                                </div>
                            )}
                        </div>
                        <div className="alert alert-warning py-2 mt-3 mb-0">
                            <small className="d-flex align-items-center">
                                <i className="mdi mdi-information-outline me-2"></i>
                                To proceed with the current GP values, please authenticate or click "Cancel" to revise the prices.
                            </small>
                        </div>
                    </div>

                    {/* Authentication Form Section */}
                    <div style={{ width: '350px' }}>
                        <Form onSubmit={handleSubmit} className="auth-container bg-white rounded-3 p-3">
                            <div className="text-center mb-3">
                                <div className="d-inline-flex align-items-center justify-content-center bg-light rounded-circle mb-2" style={{ width: "48px", height: "48px" }}>
                                    <i className="mdi mdi-shield-lock-outline text-primary" style={{ fontSize: "1.5rem" }}></i>
                                </div>
                                <h6 className="text-dark mb-0">Authentication Required</h6>
                            </div>

                            {error && (
                                <Alert color="danger" className="py-2 mb-3">
                                    <small><i className="mdi mdi-alert me-2"></i>{error}</small>
                                </Alert>
                            )}

                            <FormGroup className="mb-3">
                                <Label for="username" className="text-dark fw-medium small mb-1">Username</Label>
                                <div className="input-group input-group-sm bg-light rounded">
                                    <span className="input-group-text bg-transparent border-0">
                                        <i className="mdi mdi-account-outline text-muted"></i>
                                    </span>
                                    <Input
                                        id="username"
                                        ref={usernameRef}
                                        value={username}
                                        onChange={(e) => {
                                            setError("");
                                            e.target.value = e.target.value.toUpperCase();
                                            if (e.target.value.length > 30) {
                                                setError("Username cannot exceed 30 characters");
                                                return;
                                            }
                                            setUsername(e.target.value);
                                        }}
                                        placeholder="Enter username"
                                        autoComplete="off"
                                        className="form-control-sm border-0 bg-transparent"
                                    />
                                </div>
                            </FormGroup>

                            <FormGroup className="mb-3">
                                <Label for="password" className="text-dark fw-medium small mb-1">Password</Label>
                                <div className="input-group input-group-sm bg-light rounded">
                                    <span className="input-group-text bg-transparent border-0">
                                        <i className="mdi mdi-lock-outline text-muted"></i>
                                    </span>
                                    <Input
                                        id="password"
                                        type="password"
                                        value={password}
                                        onChange={(e) => {
                                            setError("");
                                            if (e.target.value.length > 30) {
                                                setError("Password cannot exceed 30 characters");
                                                return;
                                            }
                                            setPassword(e.target.value);
                                        }}
                                        placeholder="Enter password"
                                        autoComplete="off"
                                        className="form-control-sm border-0 bg-transparent"
                                    />
                                </div>
                            </FormGroup>

                            <div className="d-flex gap-2 mt-3 pt-2 border-top">
                                <Button
                                    color="primary"
                                    type="submit"
                                    size="sm"
                                    className="w-50"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-1" />
                                            Verifying
                                        </>
                                    ) : (
                                        <>
                                            <i className="mdi mdi-shield-check-outline me-1"></i>
                                            Authorize
                                        </>
                                    )}
                                </Button>
                                <Button
                                    color="light"
                                    size="sm"
                                    onClick={toggle}
                                    className="w-50"
                                    disabled={loading}
                                >
                                    <i className="mdi mdi-close me-1"></i>
                                    Cancel
                                </Button>
                            </div>
                        </Form>
                    </div>
                </div>
            </ModalBody>
        </Modal>
    );
};

ModelUserPermission.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    title: PropTypes.string,
    data: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        name: PropTypes.string.isRequired,
        description: PropTypes.string
    })),
    onConfirm: PropTypes.func.isRequired,
    loading: PropTypes.bool
};

export default ModelUserPermission;
